import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageContext";

/**
 * All stored translations for one entity type in the active language, keyed by
 * the row's database UUID.
 *
 * One request per entity per language for the whole catalogue — cards, city
 * pages, carousels, map popups and search results read from this shared cache,
 * so switching to Finnish translates every list on the site, not just the
 * detail pages.
 */
export function useEntityTranslationMap(
  entity: string,
  fields: string[],
): Record<string, Record<string, string>> {
  const { lang } = useLanguage();

  const { data } = useQuery({
    queryKey: ["entity-translations", entity, lang, fields.join(",")],
    enabled: lang !== "en",
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("content_translations")
        .select("entity_id, field, value")
        .eq("entity", entity)
        .eq("lang", lang)
        .in("field", fields);
      if (error) throw error;
      const map: Record<string, Record<string, string>> = {};
      for (const row of data ?? []) {
        if (typeof row.value !== "string" || !row.value.trim()) continue;
        (map[row.entity_id] ??= {})[row.field] = row.value;
      }
      return map;
    },
  });

  return data ?? {};
}

const PLACE_FIELDS = ["name", "description", "area", "category"];

export function usePlaceTranslationMap() {
  return useEntityTranslationMap("place", PLACE_FIELDS);
}

export function placeTranslationsQueryOptions(lang: string) {
  return {
    queryKey: ["entity-translations", "place", lang, PLACE_FIELDS.join(",")],
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("content_translations")
        .select("entity_id, field, value")
        .eq("entity", "place")
        .eq("lang", lang)
        .in("field", PLACE_FIELDS);
      if (error) throw error;
      const map: Record<string, Record<string, string>> = {};
      for (const row of data ?? []) {
        if (typeof row.value !== "string" || !row.value.trim()) continue;
        (map[row.entity_id] ??= {})[row.field] = row.value;
      }
      return map;
    },
  };
}
